import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, RefreshCw, Heart, MessageCircle, AlertTriangle, Brain,
  Smile, Meh, Frown, HelpCircle, PlayCircle, Play, X,
  TrendingUp, Clock, ExternalLink, Activity
} from 'lucide-react';
import { useToast } from './ToastContainer';
import api from '../utils/api';
import './TikTok.css';

const SORT_OPTIONS = [
  { value: 'newest',       label: 'Newest first' },
  { value: 'oldest',       label: 'Oldest first' },
  { value: 'most_liked',   label: 'Most liked' },
  { value: 'most_replies', label: 'Most replies' }
];

const QUICK_FILTERS = [
  { value: 'all',        label: 'All' },
  { value: 'urgent',     label: 'Urgent' },
  { value: 'questions',  label: 'Questions' },
  { value: 'complaints', label: 'Complaints' },
  { value: 'positive',   label: 'Positive' },
  { value: 'negative',   label: 'Negative' },
  { value: 'neutral',    label: 'Neutral' }
];

// Stable initial-based avatar. No network, no flicker — colour is a deterministic
// hash of the username so each commenter keeps the same tile across renders.
function InitialAvatar({ name, size = 42 }) {
  const raw = (name || '?').trim();
  const initial = (raw[0] || '?').toUpperCase();
  let hash = 0;
  for (let i = 0; i < raw.length; i++) hash = (hash * 31 + raw.charCodeAt(i)) | 0;
  const hue = Math.abs(hash) % 360;
  const bg = `linear-gradient(135deg, hsl(${hue}, 55%, 40%) 0%, hsl(${(hue + 35) % 360}, 55%, 28%) 100%)`;
  return (
    <div
      className="tt-avatar"
      style={{
        width: size, height: size, background: bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff', fontWeight: 700, fontSize: size * 0.42,
        userSelect: 'none', textTransform: 'uppercase'
      }}
      aria-hidden="true"
    >{initial}</div>
  );
}

const JOB_TYPE_PRESETS = [
  { value: 'test',     label: 'Test (1 video, 100 comments)',          comments_per_post: 100,  max_replies_per_comment: 0,   max_total_comments: 100,  results_per_page: 1 },
  { value: 'backfill', label: 'Backfill (200 videos, 1000 comments, 200 replies)', comments_per_post: 1000, max_replies_per_comment: 200, max_total_comments: null, results_per_page: 200 },
  { value: 'daily',    label: 'Daily (10 recent videos, 200/video)',   comments_per_post: 200,  max_replies_per_comment: 50,  max_total_comments: null, results_per_page: 10 },
  { value: 'manual',   label: 'Manual (custom)',                        comments_per_post: 100,  max_replies_per_comment: 0,   max_total_comments: null, results_per_page: 5 }
];

const STATUS_COLORS = {
  pending:    '#9ca3af',
  scraping:   '#3b82f6',
  fetching:   '#8b5cf6',
  analyzing:  '#f59e0b',
  completed:  '#10b981',
  failed:     '#ef4444'
};

const TikTok = () => {
  const { showError, showSuccess } = useToast();

  // Accounts + selection
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');

  // Stats / jobs
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);

  // Comments + analyses
  const [comments, setComments] = useState([]);
  const [analysisMap, setAnalysisMap] = useState({});
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [quickFilter, setQuickFilter] = useState('all');
  const [includeReplies, setIncludeReplies] = useState(true);
  const [onlyAnalyzed, setOnlyAnalyzed] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [commentsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Scrape modal + job form state
  const [showScrapeModal, setShowScrapeModal] = useState(false);
  const [scrapeJobType, setScrapeJobType] = useState('test');
  const [scrapeCommentsPerPost, setScrapeCommentsPerPost] = useState(100);
  const [scrapeMaxReplies, setScrapeMaxReplies] = useState(0);
  const [scrapeMaxTotal, setScrapeMaxTotal] = useState(100);
  const [scrapeResultsPerPage, setScrapeResultsPerPage] = useState(1);
  const [scrapeProfileSorting, setScrapeProfileSorting] = useState('latest');
  const [scrapePostUrls, setScrapePostUrls] = useState('');
  const [scrapeOldestDate, setScrapeOldestDate] = useState('');
  const [scrapeLoading, setScrapeLoading] = useState(false);

  // Queue driver
  const [runningQueue, setRunningQueue] = useState(false);

  // Selected comment modal
  const [selectedComment, setSelectedComment] = useState(null);
  const [modalReplies, setModalReplies] = useState([]);
  const [loadingReplies, setLoadingReplies] = useState(false);

  // ============ fetchers ============
  const fetchAccounts = useCallback(async () => {
    try {
      const res = await api.get('/api/tiktok/accounts');
      if (res.data.success) {
        setAccounts(res.data.accounts);
        if (!selectedAccountId && res.data.accounts.length > 0) {
          setSelectedAccountId(res.data.accounts[0].id);
        }
      }
    } catch (e) {
      console.error('fetchAccounts failed', e);
    }
  }, [selectedAccountId]);

  const fetchStats = useCallback(async () => {
    if (!selectedAccountId) return;
    try {
      const res = await api.get(`/api/tiktok/stats?account_id=${selectedAccountId}`);
      if (res.data.success) setStats(res.data.stats);
    } catch (e) { console.error('fetchStats failed', e); }
  }, [selectedAccountId]);

  const fetchJobs = useCallback(async () => {
    if (!selectedAccountId) return;
    try {
      const res = await api.get(`/api/tiktok/jobs?account_id=${selectedAccountId}&limit=10`);
      if (res.data.success) setJobs(res.data.jobs);
    } catch (e) { console.error('fetchJobs failed', e); }
  }, [selectedAccountId]);

  const fetchComments = useCallback(async () => {
    if (!selectedAccountId) return;
    try {
      setLoading(true);
      const params = new URLSearchParams({
        account_id: selectedAccountId,
        page: currentPage,
        limit: commentsPerPage,
        sort_by: sortBy,
        include_replies: includeReplies ? 'true' : 'false'
      });
      if (searchTerm) params.append('search', searchTerm);
      if (onlyAnalyzed) params.append('analyzed_only', 'true');
      const res = await api.get(`/api/tiktok/comments?${params}`);
      if (res.data.success) {
        setComments(res.data.comments);
        setTotalCount(res.data.total);
        setTotalPages(res.data.pagination.totalPages);
      }
    } catch (e) {
      console.error('fetchComments failed', e);
      setComments([]);
    } finally {
      setLoading(false);
    }
  }, [selectedAccountId, currentPage, commentsPerPage, sortBy, includeReplies, searchTerm, onlyAnalyzed]);

  const fetchAnalyses = useCallback(async () => {
    if (!selectedAccountId) return;
    try {
      const res = await api.get(`/api/ai/tiktok-comment-analysis?account_id=${selectedAccountId}&limit=5000`);
      if (res.data.success) {
        const map = {};
        for (const a of (res.data.analyses || [])) map[a.comment_id] = a;
        setAnalysisMap(map);
      }
    } catch (e) { console.error('fetchAnalyses failed', e); }
  }, [selectedAccountId]);

  // ============ effects ============
  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);
  useEffect(() => { fetchStats(); fetchJobs(); fetchAnalyses(); }, [fetchStats, fetchJobs, fetchAnalyses]);
  useEffect(() => { fetchComments(); }, [fetchComments]);

  // Fetch replies when a comment with replies is opened
  useEffect(() => {
    const c = selectedComment?.comment;
    if (!c || c.parent_comment_id || !c.reply_count || c.reply_count <= 0) {
      setModalReplies([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingReplies(true);
        const params = new URLSearchParams({
          account_id: selectedAccountId,
          parent_comment_id: c.comment_id,
          limit: 200,
          sort_by: 'oldest'
        });
        const res = await api.get(`/api/tiktok/comments?${params}`);
        if (!cancelled && res.data.success) {
          setModalReplies(res.data.comments || []);
        }
      } catch (e) {
        if (!cancelled) console.error('fetchReplies failed', e);
      } finally {
        if (!cancelled) setLoadingReplies(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedComment, selectedAccountId]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => { setSearchTerm(searchInput); setCurrentPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Auto-refresh jobs + stats while there is an active (non-terminal) job
  useEffect(() => {
    const hasActive = jobs.some(j => ['pending','scraping','fetching','analyzing'].includes(j.status));
    if (!hasActive) return;
    const t = setInterval(() => { fetchJobs(); fetchStats(); }, 5000);
    return () => clearInterval(t);
  }, [jobs, fetchJobs, fetchStats]);

  // ============ scrape actions ============
  const openScrapeModal = (preset = 'test') => {
    const p = JOB_TYPE_PRESETS.find(x => x.value === preset);
    setScrapeJobType(preset);
    setScrapeCommentsPerPost(p.comments_per_post);
    setScrapeMaxReplies(p.max_replies_per_comment);
    setScrapeMaxTotal(p.max_total_comments || '');
    setScrapeResultsPerPage(p.results_per_page);
    setScrapeProfileSorting('latest');
    setScrapePostUrls('');
    setScrapeOldestDate('');
    setShowScrapeModal(true);
  };

  const submitScrape = async () => {
    try {
      setScrapeLoading(true);
      const postUrls = scrapePostUrls
        .split('\n').map(s => s.trim()).filter(Boolean);

      const payload = {
        account_id: selectedAccountId,
        job_type: scrapeJobType,
        comments_per_post: Number(scrapeCommentsPerPost) || 100,
        max_replies_per_comment: Number(scrapeMaxReplies) || 0,
        max_total_comments: scrapeMaxTotal === '' ? null : Number(scrapeMaxTotal),
        results_per_page: Number(scrapeResultsPerPage) || 1,
        profile_sorting: scrapeProfileSorting
      };
      if (postUrls.length > 0) payload.post_urls = postUrls;
      if (scrapeOldestDate) payload.oldest_comment_date = scrapeOldestDate;

      const res = await api.post('/api/tiktok/trigger-scrape', payload);
      if (res.data.success) {
        showSuccess(`Job queued — ${res.data.job.id.slice(0,8)}`);
        setShowScrapeModal(false);
        fetchJobs();
        runQueueOnce(); // immediately kick the state machine
      } else {
        showError(res.data.error || 'Failed to queue job');
      }
    } catch (e) {
      showError(e.response?.data?.error || e.message);
    } finally {
      setScrapeLoading(false);
    }
  };

  const runQueueOnce = async () => {
    try {
      setRunningQueue(true);
      const res = await api.post('/api/tiktok/run-queue');
      if (res.data.success) {
        showSuccess(`Queue advanced (${res.data.jobs_remaining || 0} remaining)`);
        fetchJobs(); fetchStats(); fetchComments(); fetchAnalyses();
      } else {
        showError(res.data.error || 'Queue run failed');
      }
    } catch (e) {
      showError(e.response?.data?.error || e.message);
    } finally {
      setRunningQueue(false);
    }
  };

  // ============ filter derived ============
  const visibleComments = useMemo(() => {
    if (quickFilter === 'all') return comments;
    return comments.filter(c => {
      const a = analysisMap[c.comment_id];
      if (!a) return quickFilter === 'all';
      if (quickFilter === 'urgent')     return a.urgency_flag;
      if (quickFilter === 'questions')  return a.is_question;
      if (quickFilter === 'complaints') return a.is_complaint;
      if (quickFilter === 'positive')   return a.sentiment === 'positive';
      if (quickFilter === 'negative')   return a.sentiment === 'negative';
      if (quickFilter === 'neutral')    return a.sentiment === 'neutral';
      return true;
    });
  }, [comments, analysisMap, quickFilter]);

  const selectedAccount = useMemo(
    () => accounts.find(a => a.id === selectedAccountId),
    [accounts, selectedAccountId]
  );

  // ============ render helpers ============
  const sentimentIcon = (s) => {
    if (s === 'positive') return <Smile size={14} color="#10b981" />;
    if (s === 'negative') return <Frown size={14} color="#ef4444" />;
    return <Meh size={14} color="#9ca3af" />;
  };

  const fmtTime = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  const activeJob = jobs.find(j => ['pending','scraping','fetching','analyzing'].includes(j.status));

  return (
    <div className="tt-page">
      {/* ================= HEADER ================= */}
      <div className="tt-header">
        <div>
          <h1 className="gradient-text">TikTok Comments</h1>
          <p className="tt-subtitle">
            {selectedAccount ? `@${selectedAccount.username}` : 'Select an account'}
            {activeJob && (
              <span className="tt-live-badge">
                <Activity size={12} />
                {activeJob.status} — {activeJob.comments_saved || 0} saved / {activeJob.comments_analyzed || 0} analyzed
              </span>
            )}
          </p>
        </div>

        <div className="tt-header-actions">
          {accounts.length > 1 && (
            <select
              className="tt-select"
              value={selectedAccountId}
              onChange={(e) => setSelectedAccountId(e.target.value)}
            >
              {accounts.map(a => (
                <option key={a.id} value={a.id}>@{a.username}</option>
              ))}
            </select>
          )}

          <button className="tt-btn" onClick={runQueueOnce} disabled={runningQueue}>
            <RefreshCw size={16} className={runningQueue ? 'spin' : ''} />
            {runningQueue ? 'Running…' : 'Process Queue'}
          </button>

          <button className="tt-btn tt-btn-primary" onClick={() => openScrapeModal('test')}>
            <Play size={16} />
            Run Scrape
          </button>
        </div>
      </div>

      {/* ================= KPI CARDS ================= */}
      {stats && (
        <div className="tt-kpi-row">
          <div className="tt-kpi">
            <MessageCircle size={18} />
            <div className="tt-kpi-label">Total comments</div>
            <div className="tt-kpi-value">{stats.totalComments.toLocaleString()}</div>
            <div className="tt-kpi-sub">{stats.topLevelComments.toLocaleString()} top-level</div>
          </div>

          <div className="tt-kpi">
            <Brain size={18} />
            <div className="tt-kpi-label">Analyzed</div>
            <div className="tt-kpi-value">{stats.analyzedCount.toLocaleString()}</div>
            <div className="tt-kpi-sub">{stats.pendingAnalysis.toLocaleString()} pending</div>
          </div>

          <div className="tt-kpi tt-kpi-urgent">
            <AlertTriangle size={18} />
            <div className="tt-kpi-label">Urgent</div>
            <div className="tt-kpi-value">{stats.urgentCount}</div>
            <div className="tt-kpi-sub">need response</div>
          </div>

          <div className="tt-kpi">
            <HelpCircle size={18} />
            <div className="tt-kpi-label">Questions</div>
            <div className="tt-kpi-value">{stats.questionsCount}</div>
            <div className="tt-kpi-sub">awaiting reply</div>
          </div>

          <div className="tt-kpi">
            <Frown size={18} />
            <div className="tt-kpi-label">Complaints</div>
            <div className="tt-kpi-value">{stats.complaintsCount}</div>
          </div>

          <div className="tt-kpi">
            <TrendingUp size={18} />
            <div className="tt-kpi-label">Sentiment</div>
            <div className="tt-kpi-value">{stats.avgSentimentScore?.toFixed(2) ?? '—'}</div>
            <div className="tt-kpi-sub">
              <span style={{ color: '#10b981' }}>+{stats.sentimentCounts.positive}</span>{' '}
              <span style={{ color: '#9ca3af' }}>±{stats.sentimentCounts.neutral}</span>{' '}
              <span style={{ color: '#ef4444' }}>−{stats.sentimentCounts.negative}</span>
            </div>
          </div>

          <div className="tt-kpi">
            <PlayCircle size={18} />
            <div className="tt-kpi-label">Videos</div>
            <div className="tt-kpi-value">{stats.videosDiscovered}</div>
            <div className="tt-kpi-sub">discovered</div>
          </div>
        </div>
      )}

      {/* ================= JOBS PANEL ================= */}
      {jobs.length > 0 && (
        <div className="tt-jobs-panel">
          <div className="tt-jobs-header">
            <span><Clock size={14} /> Recent jobs</span>
          </div>
          <div className="tt-jobs-table">
            <div className="tt-jobs-row tt-jobs-row-head">
              <span>Type</span><span>Status</span><span>Found</span>
              <span>Saved</span><span>Analyzed</span><span>Started</span><span>Error</span>
            </div>
            {jobs.map(j => (
              <div key={j.id} className="tt-jobs-row">
                <span className="tt-job-type">{j.job_type}</span>
                <span>
                  <span
                    className="tt-status-pill"
                    style={{ background: (STATUS_COLORS[j.status] || '#6b7280') + '22', color: STATUS_COLORS[j.status] || '#6b7280' }}
                  >
                    {j.status}
                    {j.retry_count > 0 && ` (retry ${j.retry_count})`}
                  </span>
                </span>
                <span>{j.comments_found || 0}</span>
                <span>{j.comments_saved || 0}</span>
                <span>{j.comments_analyzed || 0}</span>
                <span>{j.started_at ? fmtTime(j.started_at) : '—'}</span>
                <span className="tt-job-error" title={j.error || ''}>
                  {j.error ? j.error.slice(0, 60) + (j.error.length > 60 ? '…' : '') : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ================= FILTER BAR ================= */}
      <div className="tt-filter-bar">
        <div className="tt-search">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search comments, usernames…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>

        <select className="tt-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>

        <label className="tt-checkbox">
          <input
            type="checkbox"
            checked={includeReplies}
            onChange={(e) => setIncludeReplies(e.target.checked)}
          />
          Include replies
        </label>

        <label className="tt-checkbox">
          <input
            type="checkbox"
            checked={onlyAnalyzed}
            onChange={(e) => { setOnlyAnalyzed(e.target.checked); setCurrentPage(1); }}
          />
          Only analyzed
        </label>
      </div>

      <div className="tt-chip-row">
        {QUICK_FILTERS.map(f => (
          <button
            key={f.value}
            className={`tt-chip ${quickFilter === f.value ? 'active' : ''}`}
            onClick={() => setQuickFilter(f.value)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* ================= COMMENTS LIST ================= */}
      <div className="tt-comments">
        {loading && comments.length === 0 && (
          <div className="tt-empty"><RefreshCw className="spin" /> Loading…</div>
        )}
        {!loading && visibleComments.length === 0 && (
          <div className="tt-empty">
            No comments yet. {stats?.totalComments === 0 && 'Run a test scrape to get started.'}
          </div>
        )}

        <AnimatePresence>
          {visibleComments.map(c => {
            const a = analysisMap[c.comment_id];
            const isReply = !!c.parent_comment_id;
            return (
              <motion.div
                key={c.comment_id}
                dir="auto"
                className={`tt-comment ${isReply ? 'is-reply' : ''} ${a?.urgency_flag ? 'is-urgent' : ''}`}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                onClick={() => setSelectedComment({ comment: c, analysis: a })}
              >
                <InitialAvatar name={c.nickname || c.unique_id} />
                <div className="tt-comment-body">
                  <div className="tt-comment-head" dir="ltr">
                    <span className="tt-username">@{c.unique_id || 'unknown'}</span>
                    {isReply && <span className="tt-reply-badge">reply</span>}
                    <span className="tt-time">{fmtTime(c.create_time_iso)}</span>
                    {c.video_web_url && (
                      <a
                        href={c.video_web_url}
                        target="_blank"
                        rel="noreferrer"
                        className="tt-video-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink size={12} /> video
                      </a>
                    )}
                  </div>
                  <div className="tt-comment-text">{c.text || <em>(empty)</em>}</div>
                  <div className="tt-comment-foot">
                    <span><Heart size={12} /> {c.digg_count}</span>
                    <span><MessageCircle size={12} /> {c.reply_count}</span>
                    {a && (
                      <>
                        <span className="tt-sentiment-pill" data-sentiment={a.sentiment}>
                          {sentimentIcon(a.sentiment)} {a.sentiment}
                        </span>
                        {a.urgency_flag && (
                          <span className="tt-urgent-pill"><AlertTriangle size={12} /> urgent</span>
                        )}
                        {a.is_question && (
                          <span className="tt-question-pill"><HelpCircle size={12} /> question</span>
                        )}
                        {a.is_complaint && (
                          <span className="tt-complaint-pill"><Frown size={12} /> complaint</span>
                        )}
                      </>
                    )}
                    {!a && c.analysis_status === 'pending' && (
                      <span className="tt-pending-pill">awaiting AI</span>
                    )}
                    {!a && c.analysis_status === 'failed' && (
                      <span className="tt-failed-pill">analysis failed</span>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* ================= PAGINATION ================= */}
      {totalPages > 1 && (
        <div className="tt-pagination">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
          >Prev</button>
          <span>Page {currentPage} of {totalPages} ({totalCount.toLocaleString()} total)</span>
          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
          >Next</button>
        </div>
      )}

      {/* ================= SCRAPE MODAL ================= */}
      <AnimatePresence>
        {showScrapeModal && (
          <motion.div className="tt-modal-backdrop" onClick={() => setShowScrapeModal(false)}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div className="tt-modal" onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}>
              <div className="tt-modal-head">
                <h3>Run TikTok scrape</h3>
                <button onClick={() => setShowScrapeModal(false)}><X size={18} /></button>
              </div>

              <div className="tt-modal-body">
                <label className="tt-field">
                  <span>Preset</span>
                  <select
                    value={scrapeJobType}
                    onChange={(e) => {
                      const p = JOB_TYPE_PRESETS.find(x => x.value === e.target.value);
                      setScrapeJobType(p.value);
                      setScrapeCommentsPerPost(p.comments_per_post);
                      setScrapeMaxReplies(p.max_replies_per_comment);
                      setScrapeMaxTotal(p.max_total_comments || '');
                      setScrapeResultsPerPage(p.results_per_page);
                    }}
                  >
                    {JOB_TYPE_PRESETS.map(p => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </label>

                <label className="tt-field">
                  <span>Videos to scrape from profile (ignored if you paste specific URLs below)</span>
                  <input type="number" min="1" value={scrapeResultsPerPage}
                    onChange={(e) => setScrapeResultsPerPage(e.target.value)} />
                </label>

                <label className="tt-field">
                  <span>Profile video sorting</span>
                  <select value={scrapeProfileSorting}
                    onChange={(e) => setScrapeProfileSorting(e.target.value)}>
                    <option value="latest">Latest</option>
                    <option value="popular">Popular</option>
                    <option value="oldest">Oldest</option>
                  </select>
                </label>

                <label className="tt-field">
                  <span>Comments per video</span>
                  <input type="number" value={scrapeCommentsPerPost}
                    onChange={(e) => setScrapeCommentsPerPost(e.target.value)} />
                </label>

                <label className="tt-field">
                  <span>Max replies per comment</span>
                  <input type="number" value={scrapeMaxReplies}
                    onChange={(e) => setScrapeMaxReplies(e.target.value)} />
                </label>

                <label className="tt-field">
                  <span>Max total comments (safety cap — blank = no cap)</span>
                  <input type="number" value={scrapeMaxTotal}
                    onChange={(e) => setScrapeMaxTotal(e.target.value)}
                    placeholder="e.g. 100 for test" />
                </label>

                <label className="tt-field">
                  <span>Only comments after (YYYY-MM-DD — blank = all time)</span>
                  <input type="date" value={scrapeOldestDate}
                    onChange={(e) => setScrapeOldestDate(e.target.value)} />
                </label>

                <label className="tt-field">
                  <span>Specific video URLs (one per line — blank = scrape the whole account)</span>
                  <textarea
                    rows={3}
                    value={scrapePostUrls}
                    onChange={(e) => setScrapePostUrls(e.target.value)}
                    placeholder="https://www.tiktok.com/@alrugaibfurniture/video/..."
                  />
                </label>

                <div className="tt-field-note">
                  Actor: <code>clockworks/tiktok-comments-scraper</code>.
                  This creates a job row and immediately kicks the state machine.
                  Watch the Recent Jobs panel for live status.
                </div>
              </div>

              <div className="tt-modal-foot">
                <button className="tt-btn" onClick={() => setShowScrapeModal(false)}>Cancel</button>
                <button className="tt-btn tt-btn-primary" disabled={scrapeLoading} onClick={submitScrape}>
                  {scrapeLoading ? 'Queuing…' : 'Queue & run'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ================= COMMENT MODAL ================= */}
      <AnimatePresence>
        {selectedComment && (
          <motion.div className="tt-modal-backdrop" onClick={() => setSelectedComment(null)}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div className="tt-modal" onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}>
              <div className="tt-modal-head">
                <h3>@{selectedComment.comment.unique_id || 'unknown'}</h3>
                <button onClick={() => setSelectedComment(null)}><X size={18} /></button>
              </div>
              <div className="tt-modal-body">
                <div className="tt-comment-text" style={{ fontSize: 16, marginBottom: 12 }} dir="auto">
                  {selectedComment.comment.text}
                </div>
                <div className="tt-meta-row">
                  <span><Heart size={14} /> {selectedComment.comment.digg_count} likes</span>
                  <span><MessageCircle size={14} /> {selectedComment.comment.reply_count} replies</span>
                  <span><Clock size={14} /> {fmtTime(selectedComment.comment.create_time_iso)}</span>
                  {selectedComment.comment.video_web_url && (
                    <a href={selectedComment.comment.video_web_url} target="_blank" rel="noreferrer">
                      <ExternalLink size={14} /> Open video on TikTok
                    </a>
                  )}
                </div>

                {selectedComment.analysis ? (
                  <div className="tt-analysis">
                    <h4><Brain size={14} /> AI analysis</h4>
                    <div className="tt-analysis-grid">
                      <div><b>Sentiment:</b> {selectedComment.analysis.sentiment} ({selectedComment.analysis.sentiment_score?.toFixed(2)})</div>
                      <div><b>Emotion:</b> {selectedComment.analysis.detected_emotion} ({selectedComment.analysis.emotion_confidence?.toFixed(2)})</div>
                      <div><b>Priority:</b> {selectedComment.analysis.priority_score?.toFixed(2)}</div>
                      <div><b>Question:</b> {selectedComment.analysis.is_question ? 'yes' : 'no'}</div>
                      <div><b>Complaint:</b> {selectedComment.analysis.is_complaint ? 'yes' : 'no'}</div>
                      <div><b>Urgent:</b> {selectedComment.analysis.urgency_flag ? 'YES' : 'no'}</div>
                    </div>

                    {selectedComment.analysis.urgency_reason && (
                      <div className="tt-block tt-block-urgent">
                        <b>Urgency reason:</b> {selectedComment.analysis.urgency_reason}
                      </div>
                    )}
                    {selectedComment.analysis.topics?.length > 0 && (
                      <div className="tt-block">
                        <b>Topics:</b> {selectedComment.analysis.topics.join(', ')}
                      </div>
                    )}
                    {selectedComment.analysis.pros?.length > 0 && (
                      <div className="tt-block">
                        <b>Pros:</b>
                        <ul>{selectedComment.analysis.pros.map((p,i) => <li key={i}>{p}</li>)}</ul>
                      </div>
                    )}
                    {selectedComment.analysis.cons?.length > 0 && (
                      <div className="tt-block">
                        <b>Cons:</b>
                        <ul>{selectedComment.analysis.cons.map((p,i) => <li key={i}>{p}</li>)}</ul>
                      </div>
                    )}
                    {selectedComment.analysis.detected_issues?.length > 0 && (
                      <div className="tt-block">
                        <b>Issues:</b>
                        <ul>{selectedComment.analysis.detected_issues.map((p,i) => <li key={i}>{p}</li>)}</ul>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="tt-analysis tt-analysis-empty">
                    No AI analysis yet (status: {selectedComment.comment.analysis_status}).
                  </div>
                )}

                {/* ========== Replies section ========== */}
                {!selectedComment.comment.parent_comment_id && selectedComment.comment.reply_count > 0 && (
                  <div className="tt-replies">
                    <h4>
                      <MessageCircle size={14} />
                      Replies ({selectedComment.comment.reply_count})
                      {loadingReplies && <RefreshCw size={12} className="spin" style={{ marginLeft: 6 }} />}
                    </h4>

                    {!loadingReplies && modalReplies.length === 0 && (
                      <div className="tt-analysis-empty">
                        No replies scraped yet. Re-run the scrape with a higher “Max replies per comment” to fetch them.
                      </div>
                    )}

                    <div className="tt-replies-list">
                      {modalReplies.map(r => {
                        const a = analysisMap[r.comment_id];
                        return (
                          <div key={r.comment_id} dir="auto" className={`tt-reply-row ${a?.urgency_flag ? 'is-urgent' : ''}`}>
                            <InitialAvatar name={r.nickname || r.unique_id} size={32} />
                            <div className="tt-reply-body">
                              <div className="tt-reply-head" dir="ltr">
                                <span className="tt-username">@{r.unique_id || 'unknown'}</span>
                                <span className="tt-time">{fmtTime(r.create_time_iso)}</span>
                              </div>
                              <div className="tt-comment-text">{r.text || <em>(empty)</em>}</div>
                              <div className="tt-reply-foot">
                                <span><Heart size={11} /> {r.digg_count}</span>
                                {a && (
                                  <>
                                    <span className="tt-sentiment-pill" data-sentiment={a.sentiment}>
                                      {sentimentIcon(a.sentiment)} {a.sentiment}
                                    </span>
                                    {a.urgency_flag && <span className="tt-urgent-pill"><AlertTriangle size={11} /> urgent</span>}
                                    {a.is_question && <span className="tt-question-pill"><HelpCircle size={11} /> question</span>}
                                    {a.is_complaint && <span className="tt-complaint-pill"><Frown size={11} /> complaint</span>}
                                  </>
                                )}
                                {!a && r.analysis_status === 'pending' && <span className="tt-pending-pill">awaiting AI</span>}
                                {!a && r.analysis_status === 'skipped' && <span className="tt-pending-pill">skipped (emoji/short)</span>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TikTok;
